import { IdentifierType, Metadata, SyncStatus, UpdateDataConnectionMetaDataMutationVariables } from './../../../../API';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ApiService } from '../../../shared/services/api.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  GetDataConnectionQuery,
  GetDataConnectionQueryVariables,
  UpdateDataConnectionMetaDataInput,
  UpdateQueryRecordMutation,
} from '../../../../API';
import { GetDataConnection } from '../../../../graphql/queries';
import { updateDataConnectionMetaData } from '../../../../graphql/mutations';
import { Router } from '@angular/router';
import { AppService } from '../../../shared/services/app.service';
import { AT_LEAST_ONE_ALPHABET } from '../../../shared/utils/constants';

@Component({
  selector: 'app-data-connection-mappings',
  templateUrl: './data-connection-mappings.component.html',
  styleUrls: ['./data-connection-mappings.component.scss'],
})
export class DataConnectionMappingsComponent implements OnInit {
  metadataTables: Metadata[] = [];
  mappingForm: FormGroup;
  isLoading: boolean = false;
  connectionId: string | null = null;
  connectionDetails: any = null;
  loadingText: string = 'Almost done! Just loading the metadata for your connection.';
  tableName: string | null = null;
  tableScrollHeight: string = '16rem';

  identifierOptions = [
    { label: 'Unique Identifier', value: IdentifierType.UNIQUE },
    { label: 'Hashed Email', value: IdentifierType.EMAIL },
    { label: 'None', value: IdentifierType.NONE },
  ];

  constructor(
    private fb: FormBuilder,
    private activateRoute: ActivatedRoute,
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private nzMessageService: NzMessageService,
    private router: Router,
    public appService: AppService,
  ) {
    this.mappingForm = this.fb.group({
      tables: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.connectionId = this.activateRoute.snapshot.paramMap.get('connectionId');
    this.tableName = this.activateRoute.snapshot.paramMap.get('tableName');

    if (this.tableName) {
      this.appService.dataConnectionHeader = 'Edit Table Mapping';
    } else {
      this.appService.dataConnectionHeader = 'Add Table Mapping';
    }
    if (this.connectionId) {
      this.getConnectionDetails();
    }
  }

  get tablesArray(): FormArray {
    return this.mappingForm.get('tables') as FormArray;
  }

  createColumnFormGroup(column: any): FormGroup {
    return this.fb.group({
      name: [column.name],
      type: [column.type],
      identifier: [column.identifier || IdentifierType.NONE],
      parameters: [column.parameters],
      comment: [column.comment],
      description: [column.description || '', [Validators.maxLength(200), Validators.pattern(AT_LEAST_ONE_ALPHABET)]],
    });
  }

  createTableFormGroup(table: any): FormGroup {
    const columnsArray = this.fb.array<FormGroup>([]);

    table.columns.forEach((column: any) => {
      const columnsMapping = this.createColumnFormGroup(column);
      columnsArray.push(columnsMapping);
    });

    return this.fb.group({
      datasource_name: [table.datasource_name],
      database_name: [table.database_name],
      table_name: [table.table_name],
      columns: columnsArray,
      sync_status: table.sync_status == SyncStatus.PENDING ? SyncStatus.ACTIVE : table.sync_status,
      dataConnectionId: table.dataConnectionId,
      table_description: [
        table?.table_description,
        [Validators.maxLength(250), Validators.pattern(AT_LEAST_ONE_ALPHABET)],
      ],
    });
  }

  getConnectionDetails(): void {
    if (!this.connectionId) return;
    this.isLoading = true;
    this.apiService
      .query<GetDataConnectionQuery, GetDataConnectionQueryVariables>(GetDataConnection, {
        connectionId: this.connectionId,
        orgType: this.userManagementService.getOrgType(),
      })
      .then((response) => {
        this.connectionDetails = response.data?.GetDataConnection;
        if (this.connectionDetails?.metadata) {
          this.metadataTables = this.connectionDetails.metadata;
          this.initForm();

          if (this.tableName) {
            const tableExists = this.metadataTables.some((table) => table.table_name === this.tableName);
            if (tableExists) {
              this.nzMessageService.info(`Showing mappings for table: ${this.tableName}`);
            } else {
              this.nzMessageService.warning(`Table "${this.tableName}" not found in this connection`);
            }
          }
        } else {
          this.nzMessageService.warning('No metadata found for this connection');
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Error fetching connection details: ${error?.message}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  initForm(): void {
    while (this.tablesArray.length) {
      this.tablesArray.removeAt(0);
    }

    const tablesToDisplay = this.tableName
      ? this.metadataTables.filter((table) => table.table_name === this.tableName)
      : this.metadataTables;

    tablesToDisplay.forEach((table) => {
      this.tablesArray.push(this.createTableFormGroup(table));
    });

    this.mappingForm.valueChanges.subscribe(() => {
      this.validateForm();
    });

    this.validateForm();
  }

  getColumnsArray(tableIndex: number): FormArray {
    return this.tablesArray.at(tableIndex).get('columns') as FormArray;
  }

  validateForm(): boolean {
    const isValid = !this.tablesArray.value.some((item: any, tableIndex: number) => {
      const columnsArray = this.getColumnsArray(tableIndex);
      let emailCount = 0;
      let uniqueCount = 0;

      columnsArray.controls.forEach((control) => {
        const identifier = control.get('identifier')?.value;
        if (identifier === IdentifierType.EMAIL) emailCount++;
        else if (identifier === IdentifierType.UNIQUE) uniqueCount++;
      });

      return emailCount > 1 || uniqueCount > 1;
    });
    if (isValid) this.tableScrollHeight = '16rem';
    else this.tableScrollHeight = '12.5rem';
    return isValid;
  }

  saveMapping(): void {
    if (!this.isFormValid) {
      this.nzMessageService.warning(
        'Please ensure each table has exactly one email identifier and one unique identifier.',
      );
      return;
    }

    if (!this.connectionId) {
      this.nzMessageService.error('Connection ID is missing');
      return;
    }

    this.isLoading = true;

    const currentFormValue = this.mappingForm.value;

    let allTables;

    if (this.tableName) {
      const visibleTables = currentFormValue.tables;

      const allTablesMap = new Map();
      this.metadataTables.forEach((table) => {
        allTablesMap.set(table.table_name, table);
      });

      visibleTables.forEach((visibleTable: any) => {
        allTablesMap.set(visibleTable.table_name, visibleTable);
      });

      allTables = Array.from(allTablesMap.values());
    } else {
      allTables = currentFormValue.tables;
    }

    const dataConnectionInput: UpdateDataConnectionMetaDataInput = {
      connectionId: this.connectionId,
      orgType: this.connectionDetails.orgType,
      metadata: JSON.stringify(allTables),
      table_name: this.tableName || null,
      lastModifiedBy: this.userManagementService.getEmail(),
    };

    this.apiService
      .query<UpdateQueryRecordMutation, UpdateDataConnectionMetaDataMutationVariables>(updateDataConnectionMetaData, {
        dataConnectionInput,
      })
      .then((response: any) => {
        if (response && response.data?.updateDataConnectionMetaData) {
          this.nzMessageService.success('Connection metadata updated successfully');

          this.connectionDetails = {
            ...this.connectionDetails,
            metadata: JSON.stringify(allTables),
          };
          if (this.tableName) this.connectionDetails.tableName = this.tableName;

          this.router.navigate(['/data-connection']);
        } else {
          this.nzMessageService.error('Failed to update connection metadata');
        }
      })
      .catch((error: any) => {
        this.nzMessageService.error(`Error updating connection metadata: ${error?.message}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  get isFormValid(): boolean {
    return this.validateForm() && this.mappingForm.valid;
  }
}
